import React from "react";

import { graphql } from "gatsby";

import styled from "@emotion/styled";

import { env } from "../../../config";
import AdvertisingDoubleShape from "./AdvertisingDoubleShape";
import Articles from "./Articles";
import Blogs from "./Blogs";
import BusinessCardGrid from "./BusinessCardGrid";
import Calculator from "./Calculator";
import CalculatorWithOutput from "./CalculatorWithOutput";
import Carousel from "./Carousel";
import Collapsible from "./Collapsible";
import ContactForm from "./ContactForm";
import ContentElement from "./ContentElement";
import CrossSelling from "./CrossSelling";
import DoubleShape from "./DoubleShape";
import DownloadItems from "./DownloadItems";
import Finder from "./Finder";
import GasStationsLunchOffers from "./GasStationsLunchOffers";
import HelChart from "./HelChart";
import HelNewTrend from "./HelNewTrend";
import HeroSlider from "./HeroSlider";
import HoyerCampusRestaurantMenu from "./HoyerCampusRestaurantMenu";
import Image from "./Image";
import ImgShape from "./ImgShape";
import JumpMark from "./JumpMark";
import KeyFacts from "./KeyFacts";
import LimitedInTime from "./LimitedInTime";
import LinkListGrid from "./LinkListGrid";
import LngCalculator from "./LngCalculator";
import LngChart from "./LngChart";
import LngContractModels from "./LngContractModels";
import OilFinder from "./OilFinder";
import OilfoxColumns from "./OilfoxColumns";
import PrebuiltContactForm from "./PrebuiltContactForm";
import ProductShapeList from "./ProductShapeList";
import ProductTiles from "./ProductTiles";
import ShapeOverImg from "./ShapeOverImg";
import ShopwareStorefront from "./ShopwareStorefront";
import SideNavContainer from "./SideNavContainer";
import SimCards from "./SimCards";
import StudyRotation from "./StudyRotation";
import TechnicCatalog from "./TechnicCatalog";
import TextImgGrid from "./TextImgGrid";
import ThgColumns from "./ThgColumns";
import ThreeGoodReasons from "./ThreeGoodReasons";
import Tracking from "./Tracking";
import VacancyList from "./VacancyList";
import VacancyRating from "./VacancyRating";
import Video from "./Video";
import WorkshopContactForm from "./WorkshopContactForm";
import WorkshopProductPackages from "./WorkshopProductPackages";

const Margin = styled.div`
    margin-top: ${({ top }) => Number(top) * 40}px;
    margin-bottom: ${({ bottom }) => Number(bottom) * 40}px;
`;

const ElementWrapperTitleElementWrapper = styled.div`
    position: sticky;
    top: calc(8vh * 1.1);
    z-index: 999999;
`;

const ElementWrapperTitle = styled.div`
    position: absolute;
    top: 0;
    ${({ theme }) =>
        theme.breakpoints.propPerEachFrom(
            "left",
            theme.paddings.containerOuter.values
        )};
    transform: translateY(-100%);
    background: ${({ theme }) => theme.colors.blue.toString()};
    transition: 200ms background-color;
    border-radius: 5px 5px 0 0;
    padding: 5px 10px;
    color: white;
`;

const ElementWrapper = styled(({ __typename, children, ...props }) => (
    <div {...props}>
        <ElementWrapperTitleElementWrapper>
            <ElementWrapperTitle>{__typename}</ElementWrapperTitle>
        </ElementWrapperTitleElementWrapper>
        {children}
    </div>
))`
    border: 2px solid ${({ theme }) => theme.colors.blue.toString()};
    border-left: 0;
    border-right: 0;
    position: relative;
    transition: 200ms border-color;

    ${({ theme }) => theme.selectors.hover.css`
        border-color: ${theme.colors.blue.lighten(2).toString()};

        ${ElementWrapperTitle} {
            background: ${theme.colors.blue.lighten(2).toString()};
        }
    `}
`;

const ElementWrapperIfEnabled = ({ children, ...props }) => {
    if (env !== "development") return children;

    const showElementWrapper = JSON.parse(
        localStorage.__devShowElementWrapper ?? "false"
    );

    if (showElementWrapper)
        return <ElementWrapper {...props}>{children}</ElementWrapper>;
    return children;
};

const Element = ({
    __typename,
    allEntriesLinkText,
    appParameter,
    bottomText,
    businessCards,
    buttonLabel,
    calcType,
    contactFormId,
    contactFormName,
    contactItems,
    container,
    contracts,
    cta,
    defaultElements,
    downloadItems,
    email,
    end,
    endpoint,
    eventTracking,
    facts,
    gridStyle,
    headline,
    headlineNumber,
    helpCollapsible,
    heroes,
    id,
    image,
    imgPositionX,
    imgPositionY,
    inactiveText,
    isActive,
    isItemCenter,
    isSmallShape,
    items,
    itemsDesktop,
    itemsDesktopLarge,
    itemsMobile,
    itemsTablet,
    jumpMark,
    lang,
    leftColumn,
    leftItems,
    link,
    linkOptional,
    linkText,
    margin,
    mobileImage,
    mobileOnlyThree,
    name,
    navElements,
    nestedElements,
    oilFinder,
    paddingBottom,
    paddingRight,
    paddingTop,
    phone,
    price,
    priceSubtext,
    priceText,
    productPackages,
    properties,
    queryString,
    rightColumn,
    rightItems,
    rotationItems,
    shapeRight,
    shopwareMainCategoryId,
    side,
    size,
    start,
    submit,
    table,
    teamName,
    technicCatalog,
    text,
    textAlign,
    time,
    topText,
    types,
    url,
    useFormalAddress,
    width,
    withColumns,
}) => (
    <Margin {...margin}>
        <ElementWrapperIfEnabled {...{ __typename }}>
            {{
                ElementAdvertisingDoubleShape: () => (
                    <AdvertisingDoubleShape
                        headline={headline ?? undefined}
                        headlineNumber={headlineNumber ?? undefined}
                        topText={topText ?? undefined}
                        table={table ?? undefined}
                        bottomText={bottomText ?? undefined}
                        priceText={priceText ?? undefined}
                        price={price ?? undefined}
                        priceSubtext={priceSubtext ?? undefined}
                        contactItems={contactItems ?? undefined}
                        image={image ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementArticles: () => (
                    <Articles
                        name={name ?? undefined}
                        types={types ?? undefined}
                        allEntriesLinkText={allEntriesLinkText ?? undefined}
                        linkText={linkText ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementBlogs: () => (
                    <Blogs
                        name={name ?? undefined}
                        types={types ?? undefined}
                        allEntriesLinkText={allEntriesLinkText ?? undefined}
                        linkText={linkText ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementBusinessCardGrid: () => (
                    <BusinessCardGrid
                        name={name ?? undefined}
                        headlineNumber={headlineNumber ?? undefined}
                        text={text ?? undefined}
                        businessCards={businessCards ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementCalculator: () => (
                    <Calculator
                        types={types ?? undefined}
                        buttonLabel={buttonLabel ?? undefined}
                        queryString={queryString ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementCalculatorWithOutput: () => (
                    <CalculatorWithOutput
                        calcType={calcType ?? undefined}
                        isActive={isActive ?? undefined}
                        inactiveText={inactiveText ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementCarousel: () => (
                    <Carousel
                        items={items ?? undefined}
                        textAlign={textAlign ?? undefined}
                        isItemCenter={isItemCenter ?? undefined}
                        itemsMobile={itemsMobile ?? undefined}
                        itemsTablet={itemsTablet ?? undefined}
                        itemsDesktop={itemsDesktop ?? undefined}
                        itemsDesktopLarge={itemsDesktopLarge ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementCollapsible: () => (
                    <Collapsible
                        name={name ?? undefined}
                        headlineNumber={headlineNumber ?? undefined}
                        items={items ?? undefined}
                        container={container ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementContactForm: () => (
                    <ContactForm
                        id={id ?? undefined}
                        endpoint={endpoint ?? undefined}
                        buttonLabel={buttonLabel ?? undefined}
                        headline={headline ?? undefined}
                        items={items ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementContentElement: () => (
                    <ContentElement
                        items={items ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementCrossSelling: () => (
                    <CrossSelling
                        text={text ?? undefined}
                        image={image ?? undefined}
                        link={link ?? undefined}
                        side={side ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementDoubleShape: () => (
                    <DoubleShape
                        items={items ?? undefined}
                        paddingTop={paddingTop ?? undefined}
                        paddingBottom={paddingBottom ?? undefined}
                        paddingRight={paddingRight ?? undefined}
                        width={width ?? undefined}
                        shapeRight={shapeRight ?? undefined}
                        id={id ?? undefined}
                        image={image ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementDownloadItems: () => (
                    <DownloadItems
                        name={name ?? undefined}
                        headlineNumber={headlineNumber ?? undefined}
                        downloadItems={downloadItems ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementFinder: () => (
                    <Finder
                        name={name ?? undefined}
                        submit={submit ?? undefined}
                        text={text ?? undefined}
                        image={image ?? undefined}
                        properties={properties ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementGasStationsLunchOffers: () => (
                    <GasStationsLunchOffers
                        text={text ?? undefined}
                        headlineNumber={headlineNumber ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementHelChart: () => (
                    <HelChart
                        name={name ?? undefined}
                        text={text ?? undefined}
                        container={container ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementHelNewTrend: () => (
                    <HelNewTrend
                        name={name ?? undefined}
                        container={container ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementHeroSlider: () => (
                    <HeroSlider
                        heroes={heroes ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementHoyerCampusRestaurantMenu: () => (
                    <HoyerCampusRestaurantMenu
                        text={text ?? undefined}
                        headlineNumber={headlineNumber ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementImage: () => (
                    <Image
                        size={size ?? undefined}
                        image={image ?? undefined}
                        text={text ?? undefined}
                        mobileImage={mobileImage ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementImgShape: () => (
                    <ImgShape
                        text={text ?? undefined}
                        headlineNumber={headlineNumber ?? undefined}
                        image={image ?? undefined}
                        imgPositionX={imgPositionX ?? undefined}
                        imgPositionY={imgPositionY ?? undefined}
                        isSmallShape={isSmallShape ?? undefined}
                        side={side ?? undefined}
                        cta={cta ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementJumpMark: () => (
                    <JumpMark
                        jumpMark={jumpMark ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementKeyFacts: () => (
                    <KeyFacts
                        time={time ?? undefined}
                        facts={facts ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementLimitedInTime: () => (
                    <LimitedInTime
                        start={start ?? undefined}
                        end={end ?? undefined}
                        nestedElements={nestedElements ?? undefined}
                        defaultElements={defaultElements ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementLinkListGrid: () => (
                    <LinkListGrid
                        headline={headline ?? undefined}
                        items={items ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementLngCalculator: () => (
                    <LngCalculator
                        name={name ?? undefined}
                        text={text ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementLngChart: () => (
                    <LngChart
                        name={name ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementLngContractModels: () => (
                    <LngContractModels
                        headline={headline ?? undefined}
                        contracts={contracts ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementOilFinder: () => (
                    <OilFinder
                        oilFinder={oilFinder ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementOilfoxColumns: () => (
                    <OilfoxColumns
                        leftItems={leftItems ?? undefined}
                        rightItems={rightItems ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementPrebuiltContactForm: () => (
                    <PrebuiltContactForm
                        email={email ?? undefined}
                        phone={phone ?? undefined}
                        teamName={teamName ?? undefined}
                        useFormalAddress={useFormalAddress ?? undefined}
                        name={name ?? undefined}
                        lang={lang ?? undefined}
                        contactFormName={contactFormName ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementProductShapeList: () => (
                    <ProductShapeList
                        items={items ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementProductTiles: () => (
                    <ProductTiles
                        items={items ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementShapeOverImg: () => (
                    <ShapeOverImg
                        headlineNumber={headlineNumber ?? undefined}
                        name={name ?? undefined}
                        text={text ?? undefined}
                        withColumns={withColumns ?? undefined}
                        link={link ?? undefined}
                        image={image ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementShopwareStorefront: () => (
                    <ShopwareStorefront
                        shopwareMainCategoryId={
                            shopwareMainCategoryId ?? undefined
                        }
                        margin={margin ?? undefined}
                    />
                ),
                ElementSideNavContainer: () => (
                    <SideNavContainer
                        navElements={navElements ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementSimCards: () => (
                    <SimCards
                        mobileOnlyThree={mobileOnlyThree ?? undefined}
                        items={items ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementStudyRotation: () => (
                    <StudyRotation
                        name={name ?? undefined}
                        rotationItems={rotationItems ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementTechnicCatalog: () => (
                    <TechnicCatalog
                        technicCatalog={technicCatalog ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementTextImgGrid: () => (
                    <TextImgGrid
                        eventTracking={eventTracking ?? undefined}
                        name={name ?? undefined}
                        text={text ?? undefined}
                        linkOptional={linkOptional ?? undefined}
                        image={image ?? undefined}
                        side={side ?? undefined}
                        gridStyle={gridStyle ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementThgColumns: () => (
                    <ThgColumns
                        leftColumn={leftColumn ?? undefined}
                        rightColumn={rightColumn ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementThreeGoodReasons: () => (
                    <ThreeGoodReasons
                        name={name ?? undefined}
                        items={items ?? undefined}
                        image={image ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementTracking: () => (
                    <Tracking
                        appParameter={appParameter ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementVacancyList: () => (
                    <VacancyList
                        name={name ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementVacancyRating: () => (
                    <VacancyRating
                        linkOptional={linkOptional ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementVideo: () => (
                    <Video
                        url={url ?? undefined}
                        image={image ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementWorkshopContactForm: () => (
                    <WorkshopContactForm
                        headline={headline ?? undefined}
                        contactFormId={contactFormId ?? undefined}
                        text={text ?? undefined}
                        helpCollapsible={helpCollapsible ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
                ElementWorkshopProductPackages: () => (
                    <WorkshopProductPackages
                        headline={headline ?? undefined}
                        contactFormId={contactFormId ?? undefined}
                        productPackages={productPackages ?? undefined}
                        margin={margin ?? undefined}
                    />
                ),
            }[__typename]()}
        </ElementWrapperIfEnabled>
    </Margin>
);

const Elements = ({ elements }) =>
    elements.map((props, i) => <Element key={i} {...props} />);
export default Elements;

export const query = graphql`
    fragment EntireElement on Element {
        ... on ElementAdvertisingDoubleShape {
            bottomText
            contactItems {
                email
                linkOptional {
                    href
                    text
                }
                phone
            }
            headline
            headlineNumber
            image {
                alt
                imageObject {
                    caption
                    description
                }
                src {
                    ...ImageSrc
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH

                            placeholder: BLURRED
                        )
                    }
                }
                title
            }
            margin {
                bottom
                top
            }
            price
            priceSubtext
            priceText
            table {
                left
                right
                rightStrong
            }
            topText
        }
        ... on ElementArticles {
            allEntriesLinkText
            linkText
            margin {
                bottom
                top
            }
            name
            types
        }
        ... on ElementBlogs {
            allEntriesLinkText
            linkText
            margin {
                bottom
                top
            }
            name
            types
        }
        ... on ElementBusinessCardGrid {
            businessCards {
                link {
                    anchor
                    href
                    text
                }
                mail
                mobile
                name
                phone
                position
            }
            headlineNumber
            margin {
                bottom
                top
            }
            name
            text
        }
        ... on ElementCalculator {
            buttonLabel
            margin {
                bottom
                top
            }
            queryString
            types
        }
        ... on ElementCalculatorWithOutput {
            calcType
            inactiveText
            isActive
            margin {
                bottom
                top
            }
        }
        ... on ElementCarousel {
            isItemCenter
            items {
                image {
                    alt
                    imageObject {
                        caption
                        description
                    }
                    src {
                        ...ImageSrc
                        childImageSharp {
                            gatsbyImageData(
                                layout: FULL_WIDTH

                                placeholder: BLURRED
                            )
                        }
                    }
                    title
                }
                linkOptional {
                    href
                    text
                }
                name
                sublineString
                text
            }
            itemsDesktop
            itemsDesktopLarge
            itemsMobile
            itemsTablet
            margin {
                bottom
                top
            }
            textAlign
        }
        ... on ElementCollapsible {
            container
            headlineNumber
            items {
                name
                textItems {
                    ... on CMSImage {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    ... on ElementCollapsibleItemsTextItemsMarkdown {
                        text
                    }
                    __typename
                }
            }
            margin {
                bottom
                top
            }
            name
        }
        ... on ElementContactForm {
            buttonLabel
            endpoint
            headline
            id
            items {
                name
                placeholder
                required
                validation
            }
            margin {
                bottom
                top
            }
        }
        ... on ElementContentElement {
            items {
                backgroundColor
                headlineColor
                headlineNumber
                id
                link {
                    anchor
                    href
                    text
                }
                name
                paddingBottom
                paddingTop
                text
                withColumns
            }
            margin {
                bottom
                top
            }
        }
        ... on ElementCrossSelling {
            image {
                alt
                imageObject {
                    caption
                    description
                }
                src {
                    ...ImageSrc
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH

                            placeholder: BLURRED
                        )
                    }
                }
                title
            }
            link {
                anchor
                href
                text
            }
            margin {
                bottom
                top
            }
            side
            text
        }
        ... on ElementDoubleShape {
            id
            image {
                alt
                imageObject {
                    caption
                    description
                }
                src {
                    ...ImageSrc
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH

                            placeholder: BLURRED
                        )
                    }
                }
                title
            }
            items {
                headlineNumber
                link {
                    anchor
                    href
                    text
                }
                name
                text
                wordBreak
            }
            margin {
                bottom
                top
            }
            paddingBottom
            paddingRight
            paddingTop
            shapeRight
            width
        }
        ... on ElementDownloadItems {
            downloadItems {
                image {
                    alt
                    imageObject {
                        caption
                        description
                    }
                    src {
                        ...ImageSrc
                        childImageSharp {
                            gatsbyImageData(
                                layout: FULL_WIDTH

                                placeholder: BLURRED
                            )
                        }
                    }
                    title
                }
                link {
                    anchor
                    href
                    text
                }
                text
            }
            headlineNumber
            margin {
                bottom
                top
            }
            name
        }
        ... on ElementFinder {
            image {
                alt
                imageObject {
                    caption
                    description
                }
                src {
                    ...ImageSrc
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH

                            placeholder: BLURRED
                        )
                    }
                }
                title
            }
            margin {
                bottom
                top
            }
            name
            properties
            submit
            text
        }
        ... on ElementGasStationsLunchOffers {
            headlineNumber
            margin {
                bottom
                top
            }
            text
        }
        ... on ElementHelChart {
            container
            margin {
                bottom
                top
            }
            name
            text
        }
        ... on ElementHelNewTrend {
            container
            margin {
                bottom
                top
            }
            name
        }
        ... on ElementHeroSlider {
            heroes {
                cta {
                    href
                    text
                }
                headlineNumber
                image {
                    alt
                    imageObject {
                        caption
                        description
                    }
                    src {
                        ...ImageSrc
                        childImageSharp {
                            gatsbyImageData(
                                layout: FULL_WIDTH

                                placeholder: BLURRED
                            )
                        }
                    }
                    title
                }
                imgPositionX
                imgPositionY
                isSmallShape
                side
                text
            }
            margin {
                bottom
                top
            }
        }
        ... on ElementHoyerCampusRestaurantMenu {
            headlineNumber
            margin {
                bottom
                top
            }
            text
        }
        ... on ElementImage {
            image {
                alt
                imageObject {
                    caption
                    description
                }
                src {
                    ...ImageSrc
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH

                            placeholder: BLURRED
                        )
                    }
                }
                title
            }
            margin {
                bottom
                top
            }
            mobileImage {
                alt
                imageObject {
                    caption
                    description
                }
                src {
                    ...ImageSrc
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH

                            placeholder: BLURRED
                        )
                    }
                }
                title
            }
            size
            text
        }
        ... on ElementImgShape {
            cta {
                href
                text
            }
            headlineNumber
            image {
                alt
                imageObject {
                    caption
                    description
                }
                src {
                    ...ImageSrc
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH

                            placeholder: BLURRED
                        )
                    }
                }
                title
            }
            imgPositionX
            imgPositionY
            isSmallShape
            margin {
                bottom
                top
            }
            side
            text
        }
        ... on ElementJumpMark {
            jumpMark
            margin {
                bottom
                top
            }
        }
        ... on ElementKeyFacts {
            facts {
                image {
                    alt
                    imageObject {
                        caption
                        description
                    }
                    src {
                        ...ImageSrc
                        childImageSharp {
                            gatsbyImageData(
                                layout: FULL_WIDTH

                                placeholder: BLURRED
                            )
                        }
                    }
                    title
                }
                number
                prefix
                prefixTwo
                text
            }
            margin {
                bottom
                top
            }
            time
        }
        ... on ElementLimitedInTime {
            defaultElements {
                ... on ElementAdvertisingDoubleShape {
                    bottomText
                    contactItems {
                        email
                        linkOptional {
                            href
                            text
                        }
                        phone
                    }
                    headline
                    headlineNumber
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    price
                    priceSubtext
                    priceText
                    table {
                        left
                        right
                        rightStrong
                    }
                    topText
                }
                ... on ElementArticles {
                    allEntriesLinkText
                    linkText
                    margin {
                        bottom
                        top
                    }
                    name
                    types
                }
                ... on ElementBlogs {
                    allEntriesLinkText
                    linkText
                    margin {
                        bottom
                        top
                    }
                    name
                    types
                }
                ... on ElementBusinessCardGrid {
                    businessCards {
                        link {
                            anchor
                            href
                            text
                        }
                        mail
                        mobile
                        name
                        phone
                        position
                    }
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                }
                ... on ElementCalculator {
                    buttonLabel
                    margin {
                        bottom
                        top
                    }
                    queryString
                    types
                }
                ... on ElementCalculatorWithOutput {
                    calcType
                    inactiveText
                    isActive
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementCarousel {
                    isItemCenter
                    items {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        linkOptional {
                            href
                            text
                        }
                        name
                        sublineString
                        text
                    }
                    itemsDesktop
                    itemsDesktopLarge
                    itemsMobile
                    itemsTablet
                    margin {
                        bottom
                        top
                    }
                    textAlign
                }
                ... on ElementCollapsible {
                    container
                    headlineNumber
                    items {
                        name
                        textItems {
                            ... on CMSImage {
                                alt
                                imageObject {
                                    caption
                                    description
                                }
                                src {
                                    ...ImageSrc
                                    childImageSharp {
                                        gatsbyImageData(
                                            layout: FULL_WIDTH

                                            placeholder: BLURRED
                                        )
                                    }
                                }
                                title
                            }
                            ... on ElementCollapsibleItemsTextItemsMarkdown {
                                text
                            }
                            __typename
                        }
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementContactForm {
                    buttonLabel
                    endpoint
                    headline
                    id
                    items {
                        name
                        placeholder
                        required
                        validation
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementContentElement {
                    items {
                        backgroundColor
                        headlineColor
                        headlineNumber
                        id
                        link {
                            anchor
                            href
                            text
                        }
                        name
                        paddingBottom
                        paddingTop
                        text
                        withColumns
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementCrossSelling {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    link {
                        anchor
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    side
                    text
                }
                ... on ElementDoubleShape {
                    id
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    items {
                        headlineNumber
                        link {
                            anchor
                            href
                            text
                        }
                        name
                        text
                        wordBreak
                    }
                    margin {
                        bottom
                        top
                    }
                    paddingBottom
                    paddingRight
                    paddingTop
                    shapeRight
                    width
                }
                ... on ElementDownloadItems {
                    downloadItems {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        link {
                            anchor
                            href
                            text
                        }
                        text
                    }
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementFinder {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                    properties
                    submit
                    text
                }
                ... on ElementGasStationsLunchOffers {
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    text
                }
                ... on ElementHelChart {
                    container
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                }
                ... on ElementHelNewTrend {
                    container
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementHeroSlider {
                    heroes {
                        cta {
                            href
                            text
                        }
                        headlineNumber
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        imgPositionX
                        imgPositionY
                        isSmallShape
                        side
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementHoyerCampusRestaurantMenu {
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    text
                }
                ... on ElementImage {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    mobileImage {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    size
                    text
                }
                ... on ElementImgShape {
                    cta {
                        href
                        text
                    }
                    headlineNumber
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    imgPositionX
                    imgPositionY
                    isSmallShape
                    margin {
                        bottom
                        top
                    }
                    side
                    text
                }
                ... on ElementJumpMark {
                    jumpMark
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementKeyFacts {
                    facts {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        number
                        prefix
                        prefixTwo
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    time
                }
                ... on ElementLinkListGrid {
                    headline
                    items {
                        items {
                            link {
                                anchor
                                href
                                text
                            }
                        }
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementLngCalculator {
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                }
                ... on ElementLngChart {
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementLngContractModels {
                    contracts {
                        isBestseller
                        itemHeadline
                        itemSubline
                        link {
                            anchor
                            href
                            text
                        }
                        listItems {
                            text
                        }
                    }
                    headline
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementOilFinder {
                    margin {
                        bottom
                        top
                    }
                    oilFinder
                }
                ... on ElementOilfoxColumns {
                    leftItems {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        linkOptional {
                            href
                            text
                        }
                        name
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    rightItems {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        linkOptional {
                            href
                            text
                        }
                        name
                        text
                    }
                }
                ... on ElementPrebuiltContactForm {
                    contactFormName
                    email
                    lang
                    margin {
                        bottom
                        top
                    }
                    name
                    phone
                    teamName
                    useFormalAddress
                }
                ... on ElementProductShapeList {
                    items {
                        items {
                            anchor
                            href
                            text
                        }
                        name
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementProductTiles {
                    items {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        imgPositionX
                        imgPositionY
                        name
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementShapeOverImg {
                    headlineNumber
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    link {
                        anchor
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                    withColumns
                }
                ... on ElementShopwareStorefront {
                    margin {
                        bottom
                        top
                    }
                    shopwareMainCategoryId
                }
                ... on ElementSimCards {
                    items {
                        headline {
                            hasMarginBottom
                            text
                        }
                        image {
                            image {
                                alt
                                imageObject {
                                    caption
                                    description
                                }
                                src {
                                    ...ImageSrc
                                    childImageSharp {
                                        gatsbyImageData(
                                            layout: FULL_WIDTH

                                            placeholder: BLURRED
                                        )
                                    }
                                }
                                title
                            }
                            isIcon
                        }
                        linkOptional {
                            href
                            text
                        }
                        row1 {
                            isBold
                            text
                        }
                        row2 {
                            isBold
                            text
                        }
                        subline {
                            isBold
                            text
                        }
                    }
                    margin {
                        bottom
                        top
                    }
                    mobileOnlyThree
                }
                ... on ElementStudyRotation {
                    margin {
                        bottom
                        top
                    }
                    name
                    rotationItems {
                        name
                        text
                    }
                }
                ... on ElementTechnicCatalog {
                    margin {
                        bottom
                        top
                    }
                    technicCatalog
                }
                ... on ElementTextImgGrid {
                    eventTracking
                    gridStyle
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    linkOptional {
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                    side
                    text
                }
                ... on ElementThgColumns {
                    leftColumn {
                        headline
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    rightColumn {
                        buttonCaption
                        buttonLink
                        headline
                        items {
                            image {
                                alt
                                imageObject {
                                    caption
                                    description
                                }
                                src {
                                    ...ImageSrc
                                    childImageSharp {
                                        gatsbyImageData(
                                            layout: FULL_WIDTH

                                            placeholder: BLURRED
                                        )
                                    }
                                }
                                title
                            }
                            infoText
                            name
                        }
                    }
                }
                ... on ElementThreeGoodReasons {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    items {
                        name
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementTracking {
                    appParameter {
                        onlyIfEmpty
                        value
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementVacancyList {
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementVacancyRating {
                    linkOptional {
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementVideo {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    url
                }
                ... on ElementWorkshopContactForm {
                    contactFormId
                    headline
                    helpCollapsible {
                        headline
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    text
                }
                ... on ElementWorkshopProductPackages {
                    contactFormId
                    headline
                    margin {
                        bottom
                        top
                    }
                    productPackages {
                        headline
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        price
                        text
                    }
                }
                __typename
            }
            end
            margin {
                bottom
                top
            }
            nestedElements {
                ... on ElementAdvertisingDoubleShape {
                    bottomText
                    contactItems {
                        email
                        linkOptional {
                            href
                            text
                        }
                        phone
                    }
                    headline
                    headlineNumber
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    price
                    priceSubtext
                    priceText
                    table {
                        left
                        right
                        rightStrong
                    }
                    topText
                }
                ... on ElementArticles {
                    allEntriesLinkText
                    linkText
                    margin {
                        bottom
                        top
                    }
                    name
                    types
                }
                ... on ElementBlogs {
                    allEntriesLinkText
                    linkText
                    margin {
                        bottom
                        top
                    }
                    name
                    types
                }
                ... on ElementBusinessCardGrid {
                    businessCards {
                        link {
                            anchor
                            href
                            text
                        }
                        mail
                        mobile
                        name
                        phone
                        position
                    }
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                }
                ... on ElementCalculator {
                    buttonLabel
                    margin {
                        bottom
                        top
                    }
                    queryString
                    types
                }
                ... on ElementCalculatorWithOutput {
                    calcType
                    inactiveText
                    isActive
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementCarousel {
                    isItemCenter
                    items {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        linkOptional {
                            href
                            text
                        }
                        name
                        sublineString
                        text
                    }
                    itemsDesktop
                    itemsDesktopLarge
                    itemsMobile
                    itemsTablet
                    margin {
                        bottom
                        top
                    }
                    textAlign
                }
                ... on ElementCollapsible {
                    container
                    headlineNumber
                    items {
                        name
                        textItems {
                            ... on CMSImage {
                                alt
                                imageObject {
                                    caption
                                    description
                                }
                                src {
                                    ...ImageSrc
                                    childImageSharp {
                                        gatsbyImageData(
                                            layout: FULL_WIDTH

                                            placeholder: BLURRED
                                        )
                                    }
                                }
                                title
                            }
                            ... on ElementCollapsibleItemsTextItemsMarkdown {
                                text
                            }
                            __typename
                        }
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementContactForm {
                    buttonLabel
                    endpoint
                    headline
                    id
                    items {
                        name
                        placeholder
                        required
                        validation
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementContentElement {
                    items {
                        backgroundColor
                        headlineColor
                        headlineNumber
                        id
                        link {
                            anchor
                            href
                            text
                        }
                        name
                        paddingBottom
                        paddingTop
                        text
                        withColumns
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementCrossSelling {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    link {
                        anchor
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    side
                    text
                }
                ... on ElementDoubleShape {
                    id
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    items {
                        headlineNumber
                        link {
                            anchor
                            href
                            text
                        }
                        name
                        text
                        wordBreak
                    }
                    margin {
                        bottom
                        top
                    }
                    paddingBottom
                    paddingRight
                    paddingTop
                    shapeRight
                    width
                }
                ... on ElementDownloadItems {
                    downloadItems {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        link {
                            anchor
                            href
                            text
                        }
                        text
                    }
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementFinder {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                    properties
                    submit
                    text
                }
                ... on ElementGasStationsLunchOffers {
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    text
                }
                ... on ElementHelChart {
                    container
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                }
                ... on ElementHelNewTrend {
                    container
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementHeroSlider {
                    heroes {
                        cta {
                            href
                            text
                        }
                        headlineNumber
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        imgPositionX
                        imgPositionY
                        isSmallShape
                        side
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementHoyerCampusRestaurantMenu {
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    text
                }
                ... on ElementImage {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    mobileImage {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    size
                    text
                }
                ... on ElementImgShape {
                    cta {
                        href
                        text
                    }
                    headlineNumber
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    imgPositionX
                    imgPositionY
                    isSmallShape
                    margin {
                        bottom
                        top
                    }
                    side
                    text
                }
                ... on ElementJumpMark {
                    jumpMark
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementKeyFacts {
                    facts {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        number
                        prefix
                        prefixTwo
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    time
                }
                ... on ElementLinkListGrid {
                    headline
                    items {
                        items {
                            link {
                                anchor
                                href
                                text
                            }
                        }
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementLngCalculator {
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                }
                ... on ElementLngChart {
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementLngContractModels {
                    contracts {
                        isBestseller
                        itemHeadline
                        itemSubline
                        link {
                            anchor
                            href
                            text
                        }
                        listItems {
                            text
                        }
                    }
                    headline
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementOilFinder {
                    margin {
                        bottom
                        top
                    }
                    oilFinder
                }
                ... on ElementOilfoxColumns {
                    leftItems {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        linkOptional {
                            href
                            text
                        }
                        name
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    rightItems {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        linkOptional {
                            href
                            text
                        }
                        name
                        text
                    }
                }
                ... on ElementPrebuiltContactForm {
                    contactFormName
                    email
                    lang
                    margin {
                        bottom
                        top
                    }
                    name
                    phone
                    teamName
                    useFormalAddress
                }
                ... on ElementProductShapeList {
                    items {
                        items {
                            anchor
                            href
                            text
                        }
                        name
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementProductTiles {
                    items {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        imgPositionX
                        imgPositionY
                        name
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementShapeOverImg {
                    headlineNumber
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    link {
                        anchor
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                    withColumns
                }
                ... on ElementShopwareStorefront {
                    margin {
                        bottom
                        top
                    }
                    shopwareMainCategoryId
                }
                ... on ElementSimCards {
                    items {
                        headline {
                            hasMarginBottom
                            text
                        }
                        image {
                            image {
                                alt
                                imageObject {
                                    caption
                                    description
                                }
                                src {
                                    ...ImageSrc
                                    childImageSharp {
                                        gatsbyImageData(
                                            layout: FULL_WIDTH

                                            placeholder: BLURRED
                                        )
                                    }
                                }
                                title
                            }
                            isIcon
                        }
                        linkOptional {
                            href
                            text
                        }
                        row1 {
                            isBold
                            text
                        }
                        row2 {
                            isBold
                            text
                        }
                        subline {
                            isBold
                            text
                        }
                    }
                    margin {
                        bottom
                        top
                    }
                    mobileOnlyThree
                }
                ... on ElementStudyRotation {
                    margin {
                        bottom
                        top
                    }
                    name
                    rotationItems {
                        name
                        text
                    }
                }
                ... on ElementTechnicCatalog {
                    margin {
                        bottom
                        top
                    }
                    technicCatalog
                }
                ... on ElementTextImgGrid {
                    eventTracking
                    gridStyle
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    linkOptional {
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                    side
                    text
                }
                ... on ElementThgColumns {
                    leftColumn {
                        headline
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    rightColumn {
                        buttonCaption
                        buttonLink
                        headline
                        items {
                            image {
                                alt
                                imageObject {
                                    caption
                                    description
                                }
                                src {
                                    ...ImageSrc
                                    childImageSharp {
                                        gatsbyImageData(
                                            layout: FULL_WIDTH

                                            placeholder: BLURRED
                                        )
                                    }
                                }
                                title
                            }
                            infoText
                            name
                        }
                    }
                }
                ... on ElementThreeGoodReasons {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    items {
                        name
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementTracking {
                    appParameter {
                        onlyIfEmpty
                        value
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementVacancyList {
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementVacancyRating {
                    linkOptional {
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementVideo {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    url
                }
                ... on ElementWorkshopContactForm {
                    contactFormId
                    headline
                    helpCollapsible {
                        headline
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    text
                }
                ... on ElementWorkshopProductPackages {
                    contactFormId
                    headline
                    margin {
                        bottom
                        top
                    }
                    productPackages {
                        headline
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        price
                        text
                    }
                }
                __typename
            }
            start
        }
        ... on ElementLinkListGrid {
            headline
            items {
                items {
                    link {
                        anchor
                        href
                        text
                    }
                }
                text
            }
            margin {
                bottom
                top
            }
        }
        ... on ElementLngCalculator {
            margin {
                bottom
                top
            }
            name
            text
        }
        ... on ElementLngChart {
            margin {
                bottom
                top
            }
            name
        }
        ... on ElementLngContractModels {
            contracts {
                isBestseller
                itemHeadline
                itemSubline
                link {
                    anchor
                    href
                    text
                }
                listItems {
                    text
                }
            }
            headline
            margin {
                bottom
                top
            }
        }
        ... on ElementOilFinder {
            margin {
                bottom
                top
            }
            oilFinder
        }
        ... on ElementOilfoxColumns {
            leftItems {
                image {
                    alt
                    imageObject {
                        caption
                        description
                    }
                    src {
                        ...ImageSrc
                        childImageSharp {
                            gatsbyImageData(
                                layout: FULL_WIDTH

                                placeholder: BLURRED
                            )
                        }
                    }
                    title
                }
                linkOptional {
                    href
                    text
                }
                name
                text
            }
            margin {
                bottom
                top
            }
            rightItems {
                image {
                    alt
                    imageObject {
                        caption
                        description
                    }
                    src {
                        ...ImageSrc
                        childImageSharp {
                            gatsbyImageData(
                                layout: FULL_WIDTH

                                placeholder: BLURRED
                            )
                        }
                    }
                    title
                }
                linkOptional {
                    href
                    text
                }
                name
                text
            }
        }
        ... on ElementPrebuiltContactForm {
            contactFormName
            email
            lang
            margin {
                bottom
                top
            }
            name
            phone
            teamName
            useFormalAddress
        }
        ... on ElementProductShapeList {
            items {
                items {
                    anchor
                    href
                    text
                }
                name
            }
            margin {
                bottom
                top
            }
        }
        ... on ElementProductTiles {
            items {
                image {
                    alt
                    imageObject {
                        caption
                        description
                    }
                    src {
                        ...ImageSrc
                        childImageSharp {
                            gatsbyImageData(
                                layout: FULL_WIDTH

                                placeholder: BLURRED
                            )
                        }
                    }
                    title
                }
                imgPositionX
                imgPositionY
                name
                text
            }
            margin {
                bottom
                top
            }
        }
        ... on ElementShapeOverImg {
            headlineNumber
            image {
                alt
                imageObject {
                    caption
                    description
                }
                src {
                    ...ImageSrc
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH

                            placeholder: BLURRED
                        )
                    }
                }
                title
            }
            link {
                anchor
                href
                text
            }
            margin {
                bottom
                top
            }
            name
            text
            withColumns
        }
        ... on ElementShopwareStorefront {
            margin {
                bottom
                top
            }
            shopwareMainCategoryId
        }
        ... on ElementSideNavContainer {
            margin {
                bottom
                top
            }
            navElements {
                ... on ElementAdvertisingDoubleShape {
                    bottomText
                    contactItems {
                        email
                        linkOptional {
                            href
                            text
                        }
                        phone
                    }
                    headline
                    headlineNumber
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    price
                    priceSubtext
                    priceText
                    table {
                        left
                        right
                        rightStrong
                    }
                    topText
                }
                ... on ElementArticles {
                    allEntriesLinkText
                    linkText
                    margin {
                        bottom
                        top
                    }
                    name
                    types
                }
                ... on ElementBlogs {
                    allEntriesLinkText
                    linkText
                    margin {
                        bottom
                        top
                    }
                    name
                    types
                }
                ... on ElementBusinessCardGrid {
                    businessCards {
                        link {
                            anchor
                            href
                            text
                        }
                        mail
                        mobile
                        name
                        phone
                        position
                    }
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                }
                ... on ElementCalculator {
                    buttonLabel
                    margin {
                        bottom
                        top
                    }
                    queryString
                    types
                }
                ... on ElementCalculatorWithOutput {
                    calcType
                    inactiveText
                    isActive
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementCarousel {
                    isItemCenter
                    items {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        linkOptional {
                            href
                            text
                        }
                        name
                        sublineString
                        text
                    }
                    itemsDesktop
                    itemsDesktopLarge
                    itemsMobile
                    itemsTablet
                    margin {
                        bottom
                        top
                    }
                    textAlign
                }
                ... on ElementCollapsible {
                    container
                    headlineNumber
                    items {
                        name
                        textItems {
                            ... on CMSImage {
                                alt
                                imageObject {
                                    caption
                                    description
                                }
                                src {
                                    ...ImageSrc
                                    childImageSharp {
                                        gatsbyImageData(
                                            layout: FULL_WIDTH

                                            placeholder: BLURRED
                                        )
                                    }
                                }
                                title
                            }
                            ... on ElementCollapsibleItemsTextItemsMarkdown {
                                text
                            }
                            __typename
                        }
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementContactForm {
                    buttonLabel
                    endpoint
                    headline
                    id
                    items {
                        name
                        placeholder
                        required
                        validation
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementContentElement {
                    items {
                        backgroundColor
                        headlineColor
                        headlineNumber
                        id
                        link {
                            anchor
                            href
                            text
                        }
                        name
                        paddingBottom
                        paddingTop
                        text
                        withColumns
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementCrossSelling {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    link {
                        anchor
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    side
                    text
                }
                ... on ElementDoubleShape {
                    id
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    items {
                        headlineNumber
                        link {
                            anchor
                            href
                            text
                        }
                        name
                        text
                        wordBreak
                    }
                    margin {
                        bottom
                        top
                    }
                    paddingBottom
                    paddingRight
                    paddingTop
                    shapeRight
                    width
                }
                ... on ElementDownloadItems {
                    downloadItems {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        link {
                            anchor
                            href
                            text
                        }
                        text
                    }
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementFinder {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                    properties
                    submit
                    text
                }
                ... on ElementGasStationsLunchOffers {
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    text
                }
                ... on ElementHelChart {
                    container
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                }
                ... on ElementHelNewTrend {
                    container
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementHeroSlider {
                    heroes {
                        cta {
                            href
                            text
                        }
                        headlineNumber
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        imgPositionX
                        imgPositionY
                        isSmallShape
                        side
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementHoyerCampusRestaurantMenu {
                    headlineNumber
                    margin {
                        bottom
                        top
                    }
                    text
                }
                ... on ElementImage {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    mobileImage {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    size
                    text
                }
                ... on ElementImgShape {
                    cta {
                        href
                        text
                    }
                    headlineNumber
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    imgPositionX
                    imgPositionY
                    isSmallShape
                    margin {
                        bottom
                        top
                    }
                    side
                    text
                }
                ... on ElementJumpMark {
                    jumpMark
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementKeyFacts {
                    facts {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        number
                        prefix
                        prefixTwo
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    time
                }
                ... on ElementLinkListGrid {
                    headline
                    items {
                        items {
                            link {
                                anchor
                                href
                                text
                            }
                        }
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementLngCalculator {
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                }
                ... on ElementLngChart {
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementLngContractModels {
                    contracts {
                        isBestseller
                        itemHeadline
                        itemSubline
                        link {
                            anchor
                            href
                            text
                        }
                        listItems {
                            text
                        }
                    }
                    headline
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementOilFinder {
                    margin {
                        bottom
                        top
                    }
                    oilFinder
                }
                ... on ElementOilfoxColumns {
                    leftItems {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        linkOptional {
                            href
                            text
                        }
                        name
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    rightItems {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        linkOptional {
                            href
                            text
                        }
                        name
                        text
                    }
                }
                ... on ElementPrebuiltContactForm {
                    contactFormName
                    email
                    lang
                    margin {
                        bottom
                        top
                    }
                    name
                    phone
                    teamName
                    useFormalAddress
                }
                ... on ElementProductShapeList {
                    items {
                        items {
                            anchor
                            href
                            text
                        }
                        name
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementProductTiles {
                    items {
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        imgPositionX
                        imgPositionY
                        name
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementShapeOverImg {
                    headlineNumber
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    link {
                        anchor
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                    text
                    withColumns
                }
                ... on ElementShopwareStorefront {
                    margin {
                        bottom
                        top
                    }
                    shopwareMainCategoryId
                }
                ... on ElementSimCards {
                    items {
                        headline {
                            hasMarginBottom
                            text
                        }
                        image {
                            image {
                                alt
                                imageObject {
                                    caption
                                    description
                                }
                                src {
                                    ...ImageSrc
                                    childImageSharp {
                                        gatsbyImageData(
                                            layout: FULL_WIDTH

                                            placeholder: BLURRED
                                        )
                                    }
                                }
                                title
                            }
                            isIcon
                        }
                        linkOptional {
                            href
                            text
                        }
                        row1 {
                            isBold
                            text
                        }
                        row2 {
                            isBold
                            text
                        }
                        subline {
                            isBold
                            text
                        }
                    }
                    margin {
                        bottom
                        top
                    }
                    mobileOnlyThree
                }
                ... on ElementStudyRotation {
                    margin {
                        bottom
                        top
                    }
                    name
                    rotationItems {
                        name
                        text
                    }
                }
                ... on ElementTechnicCatalog {
                    margin {
                        bottom
                        top
                    }
                    technicCatalog
                }
                ... on ElementTextImgGrid {
                    eventTracking
                    gridStyle
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    linkOptional {
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                    side
                    text
                }
                ... on ElementThgColumns {
                    leftColumn {
                        headline
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    rightColumn {
                        buttonCaption
                        buttonLink
                        headline
                        items {
                            image {
                                alt
                                imageObject {
                                    caption
                                    description
                                }
                                src {
                                    ...ImageSrc
                                    childImageSharp {
                                        gatsbyImageData(
                                            layout: FULL_WIDTH

                                            placeholder: BLURRED
                                        )
                                    }
                                }
                                title
                            }
                            infoText
                            name
                        }
                    }
                }
                ... on ElementThreeGoodReasons {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    items {
                        name
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementTracking {
                    appParameter {
                        onlyIfEmpty
                        value
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementVacancyList {
                    margin {
                        bottom
                        top
                    }
                    name
                }
                ... on ElementVacancyRating {
                    linkOptional {
                        href
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                }
                ... on ElementVideo {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    margin {
                        bottom
                        top
                    }
                    url
                }
                ... on ElementWorkshopContactForm {
                    contactFormId
                    headline
                    helpCollapsible {
                        headline
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        text
                    }
                    margin {
                        bottom
                        top
                    }
                    text
                }
                ... on ElementWorkshopProductPackages {
                    contactFormId
                    headline
                    margin {
                        bottom
                        top
                    }
                    productPackages {
                        headline
                        image {
                            alt
                            imageObject {
                                caption
                                description
                            }
                            src {
                                ...ImageSrc
                                childImageSharp {
                                    gatsbyImageData(
                                        layout: FULL_WIDTH

                                        placeholder: BLURRED
                                    )
                                }
                            }
                            title
                        }
                        price
                        text
                    }
                }
                __typename
            }
        }
        ... on ElementSimCards {
            items {
                headline {
                    hasMarginBottom
                    text
                }
                image {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    isIcon
                }
                linkOptional {
                    href
                    text
                }
                row1 {
                    isBold
                    text
                }
                row2 {
                    isBold
                    text
                }
                subline {
                    isBold
                    text
                }
            }
            margin {
                bottom
                top
            }
            mobileOnlyThree
        }
        ... on ElementStudyRotation {
            margin {
                bottom
                top
            }
            name
            rotationItems {
                name
                text
            }
        }
        ... on ElementTechnicCatalog {
            margin {
                bottom
                top
            }
            technicCatalog
        }
        ... on ElementTextImgGrid {
            eventTracking
            gridStyle
            image {
                alt
                imageObject {
                    caption
                    description
                }
                src {
                    ...ImageSrc
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH

                            placeholder: BLURRED
                        )
                    }
                }
                title
            }
            linkOptional {
                href
                text
            }
            margin {
                bottom
                top
            }
            name
            side
            text
        }
        ... on ElementThgColumns {
            leftColumn {
                headline
                text
            }
            margin {
                bottom
                top
            }
            rightColumn {
                buttonCaption
                buttonLink
                headline
                items {
                    image {
                        alt
                        imageObject {
                            caption
                            description
                        }
                        src {
                            ...ImageSrc
                            childImageSharp {
                                gatsbyImageData(
                                    layout: FULL_WIDTH

                                    placeholder: BLURRED
                                )
                            }
                        }
                        title
                    }
                    infoText
                    name
                }
            }
        }
        ... on ElementThreeGoodReasons {
            image {
                alt
                imageObject {
                    caption
                    description
                }
                src {
                    ...ImageSrc
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH

                            placeholder: BLURRED
                        )
                    }
                }
                title
            }
            items {
                name
                text
            }
            margin {
                bottom
                top
            }
            name
        }
        ... on ElementTracking {
            appParameter {
                onlyIfEmpty
                value
            }
            margin {
                bottom
                top
            }
        }
        ... on ElementVacancyList {
            margin {
                bottom
                top
            }
            name
        }
        ... on ElementVacancyRating {
            linkOptional {
                href
                text
            }
            margin {
                bottom
                top
            }
        }
        ... on ElementVideo {
            image {
                alt
                imageObject {
                    caption
                    description
                }
                src {
                    ...ImageSrc
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH

                            placeholder: BLURRED
                        )
                    }
                }
                title
            }
            margin {
                bottom
                top
            }
            url
        }
        ... on ElementWorkshopContactForm {
            contactFormId
            headline
            helpCollapsible {
                headline
                image {
                    alt
                    imageObject {
                        caption
                        description
                    }
                    src {
                        ...ImageSrc
                        childImageSharp {
                            gatsbyImageData(
                                layout: FULL_WIDTH

                                placeholder: BLURRED
                            )
                        }
                    }
                    title
                }
                text
            }
            margin {
                bottom
                top
            }
            text
        }
        ... on ElementWorkshopProductPackages {
            contactFormId
            headline
            margin {
                bottom
                top
            }
            productPackages {
                headline
                image {
                    alt
                    imageObject {
                        caption
                        description
                    }
                    src {
                        ...ImageSrc
                        childImageSharp {
                            gatsbyImageData(
                                layout: FULL_WIDTH

                                placeholder: BLURRED
                            )
                        }
                    }
                    title
                }
                price
                text
            }
        }
        __typename
    }
`;
